import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { salesOrdersApi } from "@/services/salesDocumentsApi";
import { SalesOrderStatus, SalesOrderListItem } from "@/types/salesDocuments";
import { format } from "date-fns";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const SalesOrderPickerModal = ({
  isOpen,
  onClose,
  onSelectSalesOrder,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectSalesOrder: (salesOrder: any) => void;
}) => {
  const { formatTotalAmount } = useDecimalPrecision();
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: salesOrdersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["salesorders-picker", searchTerm],
    queryFn: () => salesOrdersApi.getAll({ search: searchTerm }),
    enabled: isOpen,
  });

  const salesOrders = salesOrdersData?.data || [];

  // Filter sales orders by search term and exclude fully delivered ones
  const filteredSalesOrders = salesOrders.filter(
    (so: SalesOrderListItem) =>
      (so.codeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        so.buyerName?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      so.deliveryStatus !== "Complete" && // Exclude fully delivered
      so.status !== SalesOrderStatus.DELIVERED, // Exclude delivered status
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Pick from Sales Order</DialogTitle>
          <DialogDescription>
            Select a sales order to create delivery order. Line items will show
            available quantities.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by sales order number or buyer name..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
              aria-label="Search sales orders"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white">
                <TableRow>
                  <TableHead className="w-[150px]">SO Number</TableHead>
                  <TableHead className="w-[200px]">Buyer</TableHead>
                  <TableHead className="w-[120px]">Order Date</TableHead>
                  <TableHead className="w-[120px]">Expected Delivery</TableHead>
                  <TableHead className="w-[150px]">Amount</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[120px]">Delivery Status</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center text-red-500"
                      >
                        Error loading sales orders
                      </TableCell>
                    </TableRow>
                  ) : filteredSalesOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center">
                        No sales orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSalesOrders.map(
                      (salesOrder: SalesOrderListItem) => (
                        <TableRow
                          key={salesOrder.id}
                          className="hover:bg-muted"
                        >
                          <TableCell className="w-[150px] font-medium">
                            {salesOrder.codeNumber}
                          </TableCell>
                          <TableCell className="w-[200px]">
                            {salesOrder.buyerName || "Not Found"}
                          </TableCell>
                          <TableCell className="w-[120px]">
                            {format(
                              new Date(salesOrder.orderDate),
                              "dd/MM/yyyy",
                            )}
                          </TableCell>
                          <TableCell className="w-[120px]">
                            {salesOrder.expectedDeliveryDate
                              ? format(
                                  new Date(salesOrder.expectedDeliveryDate),
                                  "dd/MM/yyyy",
                                )
                              : "-"}
                          </TableCell>
                          <TableCell className="w-[150px]">
                            RM{" "}
                            {formatTotalAmount(Number(salesOrder.totalPayableAmount))}
                          </TableCell>
                          <TableCell className="w-[120px]">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${
                                salesOrder.status === "Draft"
                                  ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                                  : salesOrder.status === "Confirmed"
                                    ? "bg-blue-100 text-blue-800"
                                    : salesOrder.status === "Delivered"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {salesOrder.status}
                            </span>
                          </TableCell>
                          <TableCell className="w-[120px]">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-medium ${
                                salesOrder.deliveryStatus === "Not Started"
                                  ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                                  : salesOrder.deliveryStatus === "Partial"
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-green-100 text-green-800"
                              }`}
                            >
                              {salesOrder.deliveryStatus}
                            </span>
                          </TableCell>
                          <TableCell className="w-[100px]">
                            <Button
                              size="sm"
                              onClick={async () => {
                                // Fetch full sales order details
                                // The API returns the sales order object directly (not wrapped in { success, data })
                                const fullSalesOrder =
                                  await salesOrdersApi.getById(salesOrder.id);
                                onSelectSalesOrder(fullSalesOrder);
                                onClose();
                              }}
                              aria-label={`Select ${salesOrder.codeNumber}`}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesOrderPickerModal;
